import React, { useMemo, useEffect } from "react";

// Step Arena Outdoor — Summer School registration plan picker.
// Public-site mirror of stepsc-admin/.../SummerSchoolSection.js
export const SUMMER_SCHOOLS = ["Pishina outdoor - 50m", "Pishina outdoor - 15m"];
export const SUMMER_SEASONAL_MONTHS = ["June", "July", "August"];

export const SUMMER_PROGRAM_CONFIG = {
    Kids: {
        Weekday: {
            days: ["MONDAY", "WEDNESDAY", "FRIDAY"],
            times: ["10:00-11:00", "11:00-12:00", "17:30-18:30"],
            plans: {
                Weekly:   { regular: 35,  sale: 30 },
                Monthly:  { regular: 75,  sale: 65 },
                Seasonal: { regular: 150, sale: 125 },
            },
        },
        Weekend: {
            days: ["SATURDAY", "SUNDAY"],
            times: ["10:00-11:00", "11:00-12:00"],
            plans: {
                Monthly:  { regular: 60,  sale: 50 },
                Seasonal: { regular: 120, sale: 99 },
            },
        },
    },
    Adults: {
        Weekday: {
            days: ["TUESDAY", "THURSDAY"],
            times: ["09:30-10:30", "17:30-18:30"],
            plans: {
                Monthly:  { regular: 70,  sale: 59 },
                Seasonal: { regular: 160, sale: 135 },
            },
        },
        Weekend: {
            days: ["SATURDAY", "SUNDAY"],
            times: ["09:00-10:00", "10:00-11:00"],
            plans: {
                Monthly:  { regular: 70,  sale: 59 },
                Seasonal: { regular: 160, sale: 135 },
            },
        },
    },
};

const DAY_LABELS_AL = {
    MONDAY: "E hënë",
    TUESDAY: "E martë",
    WEDNESDAY: "E mërkurë",
    THURSDAY: "E enjte",
    FRIDAY: "E premte",
    SATURDAY: "E shtunë",
    SUNDAY: "E diel",
};

const PLAN_MONTHS = { Weekly: 0, Monthly: 1, Seasonal: 3 };

const seasonalStartDate = (monthName) => {
    const idx = { June: 5, July: 6, August: 7 }[monthName];
    if (idx == null) return new Date().toISOString();
    return new Date(new Date().getFullYear(), idx, 1).toISOString();
};

export const SummerSchoolSection = ({ formData, setFormData }) => {
    const program = formData.summerProgram || "";
    const planType = formData.summerPlanType || "";
    const plan = formData.summerPlan || "";

    const config = program && planType ? SUMMER_PROGRAM_CONFIG[program]?.[planType] : null;
    const planEntry = config && plan ? config.plans[plan] : null;
    const salePrice = planEntry?.sale ?? 0;
    const regularPrice = planEntry?.regular ?? salePrice;
    const planDiscount = Math.max(0, regularPrice - salePrice);

    useEffect(() => {
        if (!salePrice) return;
        setFormData((prev) => ({
            ...prev,
            totalAmount: regularPrice,
            payableAmount: salePrice,
            discount: planDiscount,
            time: PLAN_MONTHS[plan] ?? 1,
        }));
    }, [salePrice, regularPrice, planDiscount, plan, setFormData]);

    const handleProgramChange = (value) =>
        setFormData((prev) => ({
            ...prev,
            summerProgram: value,
            summerPlanType: "",
            summerPlan: "",
            summerSeasonalStartMonth: "",
            days: [],
            tempDays: [],
            totalAmount: 0,
            payableAmount: 0,
            discount: 0,
        }));

    const handlePlanTypeChange = (value) =>
        setFormData((prev) => ({
            ...prev,
            summerPlanType: value,
            summerPlan: "",
            summerSeasonalStartMonth: "",
            days: [],
            tempDays: [],
            totalAmount: 0,
            payableAmount: 0,
            discount: 0,
        }));

    const handlePlanChange = (value) => {
        const nextMonth = value === "Seasonal" ? (formData.summerSeasonalStartMonth || "June") : "";
        setFormData((prev) => ({
            ...prev,
            summerPlan: value,
            summerSeasonalStartMonth: nextMonth,
            startDate: nextMonth ? seasonalStartDate(nextMonth) : prev.startDate || new Date().toISOString(),
        }));
    };

    const handlePoolChange = (value) =>
        setFormData((prev) => ({
            ...prev,
            summerSchoolPool: value,
            swimmingPool: value,
        }));

    const slots = Array.isArray(formData.days) ? formData.days : [];

    const addSlot = () =>
        setFormData((prev) => ({
            ...prev,
            days: [...(prev.days || []), { day: "", time: "" }],
            tempDays: [...(prev.days || []), { day: "", time: "" }],
        }));

    const updateSlot = (index, key, value) =>
        setFormData((prev) => {
            const next = [...(prev.days || [])];
            next[index] = { ...next[index], [key]: value };
            return { ...prev, days: next, tempDays: next };
        });

    const removeSlot = (index) =>
        setFormData((prev) => {
            const next = (prev.days || []).filter((_, i) => i !== index);
            return { ...prev, days: next, tempDays: next };
        });

    const availablePlans = useMemo(() => (config ? Object.keys(config.plans) : []), [config]);

    return (
        <div className="flex flex-col gap-5 p-5 border-2 border-blue-500 rounded-lg bg-blue-50">
            <h2 className="text-2xl font-bold text-blue-700">Summer School — Step Arena Outdoor</h2>

            <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">Pishina</label>
                <select
                    value={formData.summerSchoolPool || ""}
                    onChange={(e) => handlePoolChange(e.target.value)}
                    className="p-2 border rounded-md"
                >
                    <option value="">Zgjedh pishinën</option>
                    {SUMMER_SCHOOLS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">Programi</label>
                <div className="flex flex-wrap gap-3">
                    {Object.keys(SUMMER_PROGRAM_CONFIG).map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => handleProgramChange(p)}
                            className={`px-4 py-2 rounded-md border-2 ${program === p ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-400"}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            {program && (
                <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">Lloji</label>
                    <div className="flex flex-wrap gap-3">
                        {Object.keys(SUMMER_PROGRAM_CONFIG[program]).map((pt) => (
                            <button
                                key={pt}
                                type="button"
                                onClick={() => handlePlanTypeChange(pt)}
                                className={`px-4 py-2 rounded-md border-2 ${planType === pt ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-400"}`}
                            >
                                {pt === "Weekday" ? "Ditët e javës" : "Fundjavë"} ({SUMMER_PROGRAM_CONFIG[program][pt].days.map(d => DAY_LABELS_AL[d]).join(", ")})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {program && planType && (
                <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">Plani</label>
                    <div className="flex flex-wrap gap-3">
                        {availablePlans.map((p) => {
                            const entry = config.plans[p];
                            const reg = entry?.regular ?? 0;
                            const sale = entry?.sale ?? 0;
                            const discounted = reg > sale;
                            const active = plan === p;
                            return (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => handlePlanChange(p)}
                                    className={`px-4 py-3 rounded-md border-2 flex flex-col items-center gap-1 ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-400"}`}
                                >
                                    <span className="font-semibold">{p}</span>
                                    <span className="text-sm flex items-baseline gap-2">
                                        {discounted && (
                                            <span className={`line-through ${active ? "text-blue-200" : "text-gray-400"}`}>
                                                {reg}€
                                            </span>
                                        )}
                                        <span className="font-bold">{sale}€</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {plan === "Seasonal" && (
                <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">Muaji i fillimit (sezonal — 3 muaj)</label>
                    <select
                        value={formData.summerSeasonalStartMonth || "June"}
                        onChange={(e) => {
                            const m = e.target.value;
                            setFormData((prev) => ({ ...prev, summerSeasonalStartMonth: m, startDate: seasonalStartDate(m) }));
                        }}
                        className="p-2 border rounded-md"
                    >
                        {SUMMER_SEASONAL_MONTHS.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
            )}

            {config && (
                <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">Ditët dhe oraret</label>
                    {slots.map((slot, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                            <select
                                value={slot.day || ""}
                                onChange={(e) => updateSlot(idx, "day", e.target.value)}
                                className="p-2 border rounded-md"
                            >
                                <option value="">Zgjedh ditën</option>
                                {config.days.map((d) => (
                                    <option key={d} value={d}>{DAY_LABELS_AL[d]}</option>
                                ))}
                            </select>
                            <select
                                value={slot.time || ""}
                                onChange={(e) => updateSlot(idx, "time", e.target.value)}
                                className="p-2 border rounded-md"
                            >
                                <option value="">Zgjedh kohën</option>
                                {config.times.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => removeSlot(idx)}
                                className="px-3 py-1 text-red-600 border border-red-400 rounded-md hover:bg-red-50"
                            >
                                Hiq
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addSlot}
                        className="self-start px-4 py-2 text-blue-600 border-2 border-blue-500 rounded-md hover:bg-blue-500 hover:text-white"
                    >
                        + Shto ditë/orë
                    </button>
                    <p className="text-sm text-gray-500">Hyrjet janë të pakufizuara për Summer School.</p>
                </div>
            )}

            {salePrice > 0 && (
                <div className="p-4 bg-white border border-blue-300 rounded-md text-blue-700 font-semibold flex items-baseline gap-2 flex-wrap">
                    <span>Çmimi:</span>
                    {planDiscount > 0 && (
                        <span className="text-gray-400 line-through font-normal">{regularPrice}€</span>
                    )}
                    <span className="font-bold">{salePrice}€</span>
                    <span className="font-normal text-blue-600">
                        ({plan}{plan === "Seasonal" && formData.summerSeasonalStartMonth ? ` — fillon në ${formData.summerSeasonalStartMonth}` : ""})
                    </span>
                </div>
            )}
        </div>
    );
};

export default SummerSchoolSection;
